'use client';

import React, { useState } from 'react';
import { Avatar, Chip, Popover, PopoverTrigger, PopoverContent, Tooltip } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useTranslations } from '../../../../contexts/translation.context';
import { ResponsiveTooltip, Typography } from '../../../../components';
import { RevshareOffer } from '../types';
import { formatTimeAgo } from '../../../../utils/formatTimeAgo';

interface OwnerCellProps {
  offer: RevshareOffer;
}

const OwnerCell: React.FC<OwnerCellProps> = ({ offer }) => {
  const { t } = useTranslations();
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  // Get owner name - handle both string and object
  const ownerName = typeof offer.owner === 'string' ? offer.name : offer.owner?.name || offer.name;

  // Get owner type
  const ownerType = typeof offer.owner === 'object' ? offer.owner?.type : 'creator';

  // Get overall popularity percent
  const popularityPercent =
    offer.of_data?.overall_popularity_percent ?? offer.overall_popularity_percent ?? 0;

  // Determine offer type display
  const typeNormalized = (offer.type || '').toString().toLowerCase();
  const isFree = typeNormalized === 'free';
  const isFreeTrial =
    typeNormalized === 'trial' ||
    typeNormalized === 'free trial' ||
    typeNormalized === 'free_trial';

  // Check deposit status
  const hasDeposit =
    offer.granted?.dep !== null && offer.granted?.dep !== undefined && offer.granted.dep > 0;

  // Shorten model ID to 4 characters (ON2519 instead of ON251936121)
  const fullId = String(offer.of_user_id || '');
  const shortId = fullId.length > 4 ? fullId.slice(-4) : fullId;

  // Get model stats from of_data for info tooltip
  const subscribers = offer.of_data?.subscribers || 0;
  const favorited = offer.of_data?.favorited || 0;
  const posts = offer.of_data?.posts || 0;
  const lastUpdate = offer.of_data?.last_update || offer.created_at;
  const timeAgo = lastUpdate ? formatTimeAgo(lastUpdate, t) : '';

  // Popover handlers for hover behavior
  const openInfo = () => setIsInfoOpen(true);
  const scheduleInfoClose = () => {
    setTimeout(() => setIsInfoOpen(false), 200);
  };

  return (
    <div className="flex items-start gap-3">
      <div className="relative">
        <Avatar
          src={offer.avatar}
          showFallback
          name={ownerName?.slice(0, 1)}
          className="w-8 h-8 rounded-2"
        />
      </div>
      <div className="flex-1">
        {/* Row 1: Owner name (creator/agency) */}
        <div className="flex items-center gap-x-2 gap-y-0 flex-wrap">
          <div className="flex items-center gap-1">
            {offer.owner.avatar ? (
              <Avatar
                src={offer.owner.avatar}
                showFallback
                name={ownerName?.slice(0, 1)}
                className="w-4 h-4 rounded-full"
              />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M18.3327 9.99935C18.3327 14.6017 14.6017 18.3327 9.99935 18.3327C5.39698 18.3327 1.66602 14.6017 1.66602 9.99935C1.66602 5.39698 5.39698 1.66602 9.99935 1.66602C14.6017 1.66602 18.3327 5.39698 18.3327 9.99935ZM12.4993 7.49935C12.4993 8.88006 11.3801 9.99935 9.99935 9.99935C8.61864 9.99935 7.49935 8.88006 7.49935 7.49935C7.49935 6.11864 8.61864 4.99935 9.99935 4.99935C11.3801 4.99935 12.4993 6.11864 12.4993 7.49935ZM9.99934 17.0827C11.486 17.0827 12.8658 16.6247 14.0051 15.842C14.5083 15.4963 14.7234 14.8379 14.4308 14.302C13.8243 13.1912 12.5745 12.4993 9.9993 12.4993C7.42409 12.4993 6.17432 13.1912 5.5678 14.302C5.27522 14.8378 5.49027 15.4963 5.99348 15.842C7.13284 16.6246 8.5126 17.0827 9.99934 17.0827Z"
                  fill="#A8A8B0"
                />
              </svg>
            )}
            <Typography variant="label2" className="whitespace-nowrap !capitalize">
              {ownerName}
            </Typography>
          </div>
        </div>

        {/* Row 2: Short ID + @username + FREE badge + info tooltip */}
        <div className="flex items-center gap-x-2 gap-y-0 flex-wrap mt-1">
          <Typography variant="label2">{shortId}</Typography>
          <a
            href={`https://onlyfans.com/${offer.of_user_name}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-0.5 hover:underline"
          >
            <Typography variant="body2" className="!text-partner">
              @{offer.of_user_name}
            </Typography>
          </a>
          {/* FREE badge */}
          {isFree && (
            <ResponsiveTooltip
              content={t('partner.revshare.offers.offerCard.freeTooltip') || 'Цена подписки'}
            >
              <span>
                <Chip
                  size="sm"
                  variant="flat"
                  classNames={{
                    base: 'bg-partner/20 text-partner h-5 cursor-pointer',
                    content: 'text-partner uppercase text-sxs font-normal',
                  }}
                >
                  FREE
                </Chip>
              </span>
            </ResponsiveTooltip>
          )}
          {/* Free-trial badge */}
          {isFreeTrial && (
            <ResponsiveTooltip
              content={
                t('partner.revshare.offers.offerCard.freeTrialTooltip') ||
                'Бесплатный пробный период'
              }
            >
              <span>
                <Chip
                  size="sm"
                  variant="flat"
                  classNames={{
                    base: 'bg-partner/20 text-partner h-5 cursor-pointer',
                    content: 'text-partner uppercase text-sxs font-normal',
                  }}
                >
                  Free-Trial
                </Chip>
              </span>
            </ResponsiveTooltip>
          )}
          {/* Model info tooltip (subscribers, likes, posts) */}
          <Popover
            placement="bottom-start"
            offset={8}
            showArrow={false}
            isOpen={isInfoOpen}
            onOpenChange={setIsInfoOpen}
            shouldCloseOnBlur
            isDismissable
          >
            <PopoverTrigger>
              <div
                className="cursor-pointer"
                onMouseEnter={openInfo}
                onMouseLeave={scheduleInfoClose}
              >
                <Icon icon="solar:info-circle-bold" width={14} className="text-neutral-500" />
              </div>
            </PopoverTrigger>
            <PopoverContent
              className="p-0 bg-white border border-light rounded-3 shadow-[0px_0px_11px_rgba(0,0,0,0.1)]"
              onMouseEnter={openInfo}
              onMouseLeave={scheduleInfoClose}
            >
              <div className="p-4 min-w-[12.5rem]">
                {/* Title */}
                <Typography variant="body2" className="text-auth-ink text-sm font-medium mb-3">
                  {t('partner.revshare.offers.infoTooltip.title') || 'Общая информация'}
                </Typography>

                {/* Stats rows */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-neutral-600">
                      {t('partner.revshare.offers.infoTooltip.subscribers') || 'Подписчики'}
                    </span>
                    <span className="text-xs font-medium text-neutral-900">
                      {subscribers.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-neutral-600">
                      {t('partner.revshare.offers.infoTooltip.likes') || 'Лайки'}
                    </span>
                    <span className="text-xs font-medium text-neutral-900">
                      {favorited.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-neutral-600">
                      {t('partner.revshare.offers.infoTooltip.posts') || 'Посты'}
                    </span>
                    <span className="text-xs font-medium text-neutral-900">
                      {posts.toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Update timestamp */}
                {timeAgo && (
                  <div className="mt-3 pt-2 ">
                    <span className="text-xxs text-neutral-600">
                      {t('partner.revshare.offers.infoTooltip.updated') || 'Обновлено'} {timeAgo}
                    </span>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Row 3: Owner type + % + Deposit status + Custom media */}
        <div className="flex flex-wrap gap-2 mt-1">
          {/* Тип владельца */}
          <Chip
            size="sm"
            variant="flat"
            classNames={{
              base: 'bg-accent/20 h-6',
              content: 'text-accent text-xs',
            }}
          >
            {ownerType === 'creator'
              ? t('global.auth.userTypes.creator')
              : t('global.auth.userTypes.agency')}
          </Chip>

          {/* Процент TOP (оранжевая плашка) */}
          <ResponsiveTooltip
            content={
              t('partner.revshare.offers.offerCard.topCreatorsTooltip') ||
              'Топ всех создателей контента'
            }
          >
            <span>
              <Chip
                size="sm"
                variant="flat"
                classNames={{
                  base: 'bg-warning/20 h-6 cursor-pointer',
                  content: 'text-warning text-xs',
                }}
              >
                {typeof popularityPercent === 'number'
                  ? popularityPercent.toFixed(0)
                  : popularityPercent}
                %
              </Chip>
            </span>
          </ResponsiveTooltip>

          {/* Статус депозита */}
          {hasDeposit ? (
            <ResponsiveTooltip
              content={
                t('partner.revshare.offers.offerCard.depositTooltip') ||
                'Платежи полностью гарантируются клиентом'
              }
            >
              <span>
                <Chip
                  size="sm"
                  variant="flat"
                  classNames={{
                    base: 'bg-success/20 h-6 cursor-pointer',
                    content: 'text-success text-xs',
                  }}
                >
                  {t('partner.revshare.offers.offerCard.depositPaid') || 'Депозит внесён'}
                </Chip>
              </span>
            </ResponsiveTooltip>
          ) : (
            <ResponsiveTooltip
              content={
                t('partner.revshare.offers.offerCard.noDepositTooltip') ||
                'В случае потери доступа к профилю гарантийная сумма от клиента отсутствует'
              }
            >
              <span>
                <Chip
                  size="sm"
                  variant="flat"
                  classNames={{
                    base: 'bg-auth-strength-weak/20 h-6 cursor-pointer',
                    content: 'text-auth-strength-weak text-xs',
                  }}
                >
                  {t('partner.revshare.offers.offerCard.noDeposit') || 'Не внесен депозит'}
                </Chip>
              </span>
            </ResponsiveTooltip>
          )}

          {/* Кастомные медиа */}
          {offer.offer?.custom_media_for_partner && (
            <Chip
              size="sm"
              variant="flat"
              classNames={{
                base: 'bg-partner/20 h-6',
                content: 'text-partner text-xs',
              }}
            >
              {t('partner.revshare.offers.offerCard.customMedia') || 'Кастомные медиа'}
            </Chip>
          )}
        </div>
      </div>
    </div>
  );
};

export default OwnerCell;
